import {useMutation} from '@tanstack/react-query';
import {type} from 'arktype';
import {AccountId} from '~/entities/operations-fiat';
import {ErroneousServerResult, expectEither, HttpClient, POST, useHttpClient} from '~/library/network-facade';

const CreateWithdrawSwiftParams = type({
    amount: 'string',
    swift: 'string',
    account: 'string',
});
type CreateWithdrawSwiftParams = typeof CreateWithdrawSwiftParams.infer;

const Order = type({
    id: 'number',
    type: 'string',
    status: 'string',
    title: 'string',
    created_at: 'string.date',
});

function createWithdrawSwiftOrder(http: HttpClient, accountId: AccountId, params: CreateWithdrawSwiftParams) {
    return http(`v1/accounts/${accountId}/send/banks/swift`, POST(params)).then(
        ...expectEither({
            200: Order,
            400: ErroneousServerResult,
        })
    );
}

export function useWithdrawSwiftOrderMutation(accountId: AccountId) {
    const http = useHttpClient();
    return useMutation({
        mutationKey: ['v1/accounts/{accountId}/send/banks/swift', accountId],
        mutationFn: async (params: CreateWithdrawSwiftParams) => {
            const body = {
                ...params,
                account: normalizeString(params.account),
                swift: normalizeString(params.swift),
            };

            const response = await createWithdrawSwiftOrder(http, accountId, body);
            const {status, json} = response;

            switch (status) {
                case 200:
                    return {value: json};
                case 400:
                    return {problem: json};
                case 'Abort':
                    throw response.reason;
            }
        },
    });
}

function normalizeString(value: string): string {
    return value.replace(/\s+/g, '').toLowerCase();
}
